import { BaseAuthStrategy } from "./base-strategy";
import { AuthFeature } from "./types";
import type {
	AuthResult,
	EmailPasswordCredentials,
	RegisteredUser,
} from "./types";

export interface EmailPasswordConfig {
	loginEndpoint: string;
	apiKey?: string;
	validateCredentials?: (credentials: EmailPasswordCredentials) => boolean;
}

export class EmailPasswordStrategy extends BaseAuthStrategy {
	readonly type = "email-password";
	readonly name = "Email & Password";
	readonly description = "Traditional email and password authentication";

	constructor(private config: EmailPasswordConfig) {
		super();
	}

	async authenticate(credentials: unknown): Promise<AuthResult> {
		try {
			const creds = this.validateCredentials(credentials);

			const response = await fetch(this.config.loginEndpoint, {
				method: "POST",
				headers: {
					"Content-Type": "application/json",
					...(this.config.apiKey && { "API-Key": this.config.apiKey }),
				},
				body: JSON.stringify(creds),
			});

			if (!response.ok) {
				const errorData = await response.json();
				return {
					success: false,
					error: errorData.message || "Authentication failed",
					details: errorData,
				};
			}

			const user: RegisteredUser = await response.json();

			return {
				success: true,
				user,
			};
		} catch (error) {
			return this.handleError(error);
		}
	}

	getRequiredFields(): string[] {
		return ["email", "password"];
	}

	supportsFeature(feature: AuthFeature): boolean {
		switch (feature) {
			case AuthFeature.PASSWORD_RESET:
			case AuthFeature.EMAIL_VERIFICATION:
				return true;
			default:
				return false;
		}
	}

	private validateCredentials(credentials: unknown): EmailPasswordCredentials {
		if (!credentials || typeof credentials !== "object") {
			throw new Error("Invalid credentials format");
		}

		const creds = credentials as Record<string, unknown>;

		if (!creds.email || typeof creds.email !== "string") {
			throw new Error("Email is required");
		}

		if (!creds.password || typeof creds.password !== "string") {
			throw new Error("Password is required");
		}

		// Additional validation if provided
		if (this.config.validateCredentials) {
			const emailPasswordCreds = {
				email: creds.email,
				password: creds.password,
			};

			if (!this.config.validateCredentials(emailPasswordCreds)) {
				throw new Error("Invalid email or password format");
			}
		}

		return {
			email: creds.email,
			password: creds.password,
		};
	}
}
